import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { api, type PropertyConfig } from '@/lib/api';
import { DEFAULT_WIDGET_SETTINGS } from '@/lib/constants';
import { trackWidgetLoad, stopAbandonmentTracking } from '@/lib/analytics';

interface PropertyContextValue {
  propertyKey: string | null;
  config: PropertyConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Whether this is a demo property (no real payments processed) */
  isDemo: boolean;
  /** Fee settings for checkout display */
  feeSettings: {
    feeRate: number;
    feeDisplayMode: 'guest_pays' | 'hotel_absorbs';
    showFeeToGuest: boolean;
  };
}

const PropertyContext = createContext<PropertyContextValue | null>(null);

interface PropertyProviderProps {
  children: ReactNode;
}

export function PropertyProvider({ children }: PropertyProviderProps) {
  const params = useParams();
  const location = useLocation();
  const [config, setConfig] = useState<PropertyConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Track if we've already sent the widget load event
  const hasTrackedLoad = useRef(false);

  // Determine property key from URL or subdomain
  const propertyKey = getPropertyKey(params, location);

  // Get instance key from URL query params (for multi-instance support)
  const getInstanceKey = (): string | null => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('instance');
    }
    return null;
  };

  const fetchConfig = useCallback(async () => {
    if (!propertyKey) {
      setError('No property key found');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if instance key is provided
      const instanceKey = getInstanceKey();

      // Fetch instance-specific config if instance key exists, otherwise use property config
      const data = instanceKey
        ? await api.getInstanceConfig(propertyKey, instanceKey)
        : await api.getPropertyConfig(propertyKey);

      setConfig(data);
      
      // Track widget load once when config is successfully fetched
      if (!hasTrackedLoad.current && data) {
        hasTrackedLoad.current = true;
        trackWidgetLoad(data.property.id, data.property.name);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load property');
    } finally {
      setIsLoading(false);
    }
  }, [propertyKey]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);
  
  // Cleanup abandonment tracking on unmount
  useEffect(() => {
    return () => {
      stopAbandonmentTracking();
    };
  }, []);

  // Default fee settings (for free tier - guest pays 5%)
  const defaultFeeSettings = {
    feeRate: 0.05,
    feeDisplayMode: 'guest_pays' as const,
    showFeeToGuest: true,
  };

  return (
    <PropertyContext.Provider
      value={{
        propertyKey,
        config,
        isLoading,
        error,
        refetch: fetchConfig,
        isDemo: config?.isDemo ?? false,
        feeSettings: config?.feeSettings ?? defaultFeeSettings,
      }}
    >
      {children}
    </PropertyContext.Provider>
  );
}

/**
 * Get property key from URL params or subdomain
 */
function getPropertyKey(
  params: Record<string, string | undefined>,
  location: ReturnType<typeof useLocation>
): string | null {
  // First check URL params (embed mode)
  if (params.propertyKey) {
    return params.propertyKey;
  }

  // Check URL path for embed routes: /embed/:propertyKey
  // This handles the case when PropertyProvider is outside Routes
  const embedMatch = location.pathname.match(/^\/embed\/([^/]+)/);
  if (embedMatch) {
    return embedMatch[1];
  }

  // Check for subdomain (landing mode)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);

    // For localhost or explicit property query param, use query param
    if (hostname === 'localhost' || hostname === '127.0.0.1' || searchParams.has('property')) {
      return searchParams.get('property') || null;
    }

    // Extract subdomain from {slug}.experiencelocal.io
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts.slice(-2).join('.') === 'experiencelocal.io') {
      const subdomain = parts[0];
      // Ignore 'widget' subdomain - it's for the demo showcase, not a property
      if (subdomain === 'widget' || subdomain === 'widget-staging') {
        return null;
      }
      return subdomain;
    }

    // For custom domains, check meta tag set by server
    const metaProperty = document.querySelector('meta[name="el-property-key"]');
    if (metaProperty) {
      return metaProperty.getAttribute('content');
    }
  }

  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePropertyContext() {
  const context = useContext(PropertyContext);
  if (!context) {
    throw new Error('usePropertyContext must be used within a PropertyProvider');
  }
  return context;
}

/**
 * Get widget settings with defaults
 * Merges config from API with URL param overrides
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWidgetSettings() {
  const { config } = usePropertyContext();
  const baseSettings = config?.widgetSettings || DEFAULT_WIDGET_SETTINGS;
  
  // Check for URL param overrides (set by embed script)
  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    
    const mobileCarousel = searchParams.get('mobileCarousel');
    const layout = searchParams.get('layout');
    const columns = searchParams.get('columns');
    const theme = searchParams.get('theme');
    const primaryColor = searchParams.get('primaryColor');
    
    return {
      ...baseSettings,
      ...(mobileCarousel === 'true' && { mobileCarousel: true }),
      ...(layout && (layout === 'grid' || layout === 'list') && { layout }),
      ...(columns && { columns: parseInt(columns, 10) }),
      ...(theme && (theme === 'light' || theme === 'dark' || theme === 'auto') && { theme }),
      ...(primaryColor && { primaryColor: decodeURIComponent(primaryColor) }),
    };
  }
  
  return baseSettings;
}
